import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Form, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';

const PatternImageTool = ({
  onPatternChange,
  onPatterApproved,
  initialPattern,
  readOnly,
  onRemove,
  disableScaleTool,
}) => {
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [scale, setScale] = useState(1);
  const [approvedPatternBase64, setApprovedPatternBase64] = useState(initialPattern || null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const canvasRef = useRef(null);
  const imgRef = useRef(new Image());
  const fileInputRef = useRef(null);

  const isUploadedToBackend = !!initialPattern && !imageFile;

  useEffect(() => {
    if (imageFile && canvasRef.current) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target.result;
        imgRef.current.src = dataUrl;
        imgRef.current.onload = () => drawPattern(scale);
      };
      reader.readAsDataURL(imageFile);
    }
  }, [imageFile, scale, offsetX, offsetY]);

  useEffect(() => {
    if (initialPattern && !imageFile && canvasRef.current) {
      const dataUrl = `data:image/jpeg;base64,${initialPattern}`;
      setPreviewUrl(dataUrl);
      setApprovedPatternBase64(initialPattern);
      imgRef.current.src = dataUrl;
      imgRef.current.onload = () => drawPattern(scale);
    }
  }, [initialPattern, imageFile]);

  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
      if (imgRef.current?.src?.startsWith('blob:')) {
        URL.revokeObjectURL(imgRef.current.src);
      }
    };
  }, [previewUrl]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml'].includes(file.type) && file.size <= 1048576) {
      setImageFile(file);
      setScale(1);
      setOffsetX(0);
      setOffsetY(0);
      setApprovedPatternBase64(null);

      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target.result;
        setPreviewUrl(dataUrl);
        imgRef.current.src = dataUrl;
        imgRef.current.onload = () => {
          if (canvasRef.current) drawPattern(1);
        };
      };
      reader.readAsDataURL(file);
    } else {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setImageFile(null);
      setPreviewUrl(null);
      toast.error('Please upload a valid JPG, PNG, GIF, or SVG image (max 1MB)', { autoClose: 5000 });
    }
  };

  const drawPattern = (scaleFactor) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const img = imgRef.current;
    if (!img.complete || img.naturalWidth === 0) return;

    canvas.width = 300;
    canvas.height = 300;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    if (scaleFactor <= 1) {
      const fitScale = Math.min(canvas.width / img.naturalWidth, canvas.height / img.naturalHeight);
      const baseWidth = img.naturalWidth * fitScale;
      const baseHeight = img.naturalHeight * fitScale;
      const magnification = 1 / scaleFactor;
      const scaledWidth = baseWidth * magnification;
      const scaledHeight = baseHeight * magnification;
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = scaledWidth;
      tempCanvas.height = scaledHeight;
      const tempCtx = tempCanvas.getContext('2d');
      tempCtx.drawImage(img, offsetX * fitScale, offsetY * fitScale, scaledWidth, scaledHeight);
      const pattern = ctx.createPattern(tempCanvas, 'repeat');
      ctx.fillStyle = pattern;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    } else {
      const naturalWidth = img.naturalWidth;
      const naturalHeight = img.naturalHeight;
      let scaledWidth, scaledHeight;

      if (naturalWidth >= naturalHeight) {
        scaledWidth = canvas.width / scaleFactor;
        scaledHeight = scaledWidth * (naturalHeight / naturalWidth);
      } else {
        scaledHeight = canvas.height / scaleFactor;
        scaledWidth = scaledHeight * (naturalWidth / naturalHeight);
      }

      const cols = Math.ceil(canvas.width / scaledWidth);
      const rows = Math.ceil(canvas.height / scaledHeight);

      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          const x = col * scaledWidth;
          const y = row * scaledHeight;
          ctx.drawImage(img, 0, 0, naturalWidth, naturalHeight, x, y, scaledWidth, scaledHeight);
        }
      }
    }

    const dataUrl = canvas.toDataURL('image/jpeg');
    setPreviewUrl(dataUrl);
    const base64 = dataUrl.split(',')[1];
    setApprovedPatternBase64(base64);
    onPatternChange(base64);
    onPatterApproved(base64);
  };

  const handleScaleChange = (e) => {
    const newScale = parseFloat(e.target.value);
    if (!Number.isNaN(newScale)) {
      setScale(newScale);
    }
  };

  const handleRemove = () => {
    setImageFile(null);
    setPreviewUrl(null);
    setApprovedPatternBase64(null);
    setScale(1);
    setOffsetX(0);
    setOffsetY(0);
    onPatterApproved(null);
    onPatternChange(null);
    if (onRemove) onRemove();
    toast.info('Image removed', { autoClose: 5000 });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleMouseDown = (e) => {
    if (!readOnly && scale < 1) {
      setIsDragging(true);
      const rect = canvasRef.current?.getBoundingClientRect();
      if (rect) {
        setStartX(e.clientX - rect.left - offsetX);
        setStartY(e.clientY - rect.top - offsetY);
      }
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && !readOnly && scale < 1 && canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      const newOffsetX = e.clientX - rect.left - startX;
      const newOffsetY = e.clientY - rect.top - startY;
      setOffsetX(newOffsetX);
      setOffsetY(newOffsetY);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  return (
    <>
      {!approvedPatternBase64 && !previewUrl && !readOnly && (
        <Row className="mb-4">
          <Col sm={12} md={6} className="d-flex align-items-center">
            <Form.Group controlId="patternImage" className="w-100">
              <Form.Label className="flex-grow-1">
                <b>Pattern Image</b>
              </Form.Label>
              <Form.Control
                type="file"
                accept=".jpg,.png,.gif,.svg"
                onChange={handleFileChange}
                disabled={readOnly}
                ref={fileInputRef}
              />
            </Form.Group>
          </Col>
        </Row>
      )}
      {(imageFile || previewUrl || approvedPatternBase64) && (
        <>
          <Row className="mb-4">
            <Col sm={6} className="d-flex flex-column justify-content-center align-items-start">
              <Form.Label className="flex-grow-1">
                <b>Preview</b>
              </Form.Label>
              <canvas
                ref={canvasRef}
                style={{
                  border: '1px solid #ccc',
                  width: '300px',
                  height: '300px',
                  cursor: readOnly || scale >= 1 ? 'default' : 'move',
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              />
            </Col>
          </Row>
          <Row className="mt-2">
            <Col sm={6}>
              {!readOnly && (
                <>
                  {!disableScaleTool && !isUploadedToBackend && imageFile && canvasRef.current && (
                    <>
                      <Form.Label className="mt-2 flex-grow-1">
                        <b>Scale ({scale.toFixed(2)}x) - Left: Zoom In, Right: Zoom Out</b>
                      </Form.Label>
                      <Form.Control
                        type="range"
                        min="0.1"
                        max="2"
                        step="0.1"
                        value={scale}
                        onChange={handleScaleChange}
                        className="mb-2"
                      />
                      <Form.Control
                        type="number"
                        min="0.1"
                        max="2"
                        step="0.1"
                        value={scale}
                        onChange={handleScaleChange}
                        className="mb-2"
                      />
                    </>
                  )}
                  <div className="d-flex gap-2 mt-2">
                    <Button variant="outline-primary" onClick={handleRemove} disabled={readOnly} className="mt-2">
                      Remove Image
                    </Button>
                  </div>
                </>
              )}
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

export default PatternImageTool;
